import React, { useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import api from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { Switch } from '../../../components/ui/Switch';
import { cn } from '../../../lib/utils';
import ShareAccountButton from './ShareAccountButton';
import type { EmailAccount } from '../useData';
import { accountSharesSwrKey, type AccountShare } from './accountShares';

const UserIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4 21a8 8 0 0116 0H4z" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const SpinnerIcon = ({ className }: { className?: string }) => (
  <svg className={cn('animate-spin', className)} fill="none" viewBox="0 0 24 24" aria-hidden>
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

interface AccountSharesSectionProps {
  account: EmailAccount;
  onMutate?: () => void;
}

export const AccountSharesSection: React.FC<AccountSharesSectionProps> = ({ account, onMutate }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [revokingShareId, setRevokingShareId] = useState<number | null>(null);
  const [updatingShareId, setUpdatingShareId] = useState<number | null>(null);

  const swrKey = accountSharesSwrKey(account.id);
  const { data: shares, isLoading, mutate } = useSWR<AccountShare[]>(
    swrKey,
    async (url: string) => {
      const response = await api.get<AccountShare[]>(url);
      return Array.isArray(response.data) ? response.data : [];
    },
  );

  const list = shares ?? [];
  const rowBusy = revokingShareId != null || updatingShareId != null;

  const refreshShares = async () => {
    await mutate();
    await globalMutate('/emails/accounts/shared-with-me');
    await globalMutate('/email-accounts/manage');
    onMutate?.();
  };

  const handleRemoveShare = async (shareId: number) => {
    setRevokingShareId(shareId);
    try {
      await api.delete(`/emails/accounts/shares/${shareId}`);
      showSuccess('Share removed');
      await refreshShares();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to remove share');
    } finally {
      setRevokingShareId(null);
    }
  };

  const handleShareFolderToggle = async (
    shareId: number,
    field: 'showSentFolder' | 'showMySentFolder' | 'showInAccountsSidebar',
    value: boolean,
  ) => {
    setUpdatingShareId(shareId);
    try {
      await api.patch(`/emails/accounts/shares/${shareId}`, { [field]: value });
      await refreshShares();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to update share settings');
    } finally {
      setUpdatingShareId(null);
    }
  };

  const shareFolderSwitch = (
    share: AccountShare,
    field: 'showSentFolder' | 'showMySentFolder' | 'showInAccountsSidebar',
    label: string,
  ) => {
    const inherited =
      field === 'showSentFolder'
        ? share.showSentFolder
        : field === 'showMySentFolder'
          ? share.showMySentFolder
          : share.showInAccountsSidebar;
    const accountDefault =
      field === 'showInAccountsSidebar'
        ? (account.showInAccountsSidebar ?? true)
        : true;
    const effective = inherited ?? accountDefault;
    const disabled = rowBusy || updatingShareId === share.shareId;

    return (
      <div className="flex items-center gap-2 min-w-[5.5rem]">
        <Switch
          checked={effective}
          disabled={disabled}
          size="sm"
          onChange={(e) => void handleShareFolderToggle(share.shareId, field, e.target.checked)}
          aria-label={`${label} for ${share.sharedWithUserName}`}
        />
        <span className={cn('text-[11px] font-medium whitespace-nowrap', colors.textMuted)}>{label}</span>
      </div>
    );
  };

  const tableHeaderClass = cn(
    'grid gap-3 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider',
    colors.textMuted,
    'bg-black/5 dark:bg-white/5',
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={cn('text-sm', colors.textMuted)}>
          Users who can access this mailbox in <strong>/emails</strong>. Toggle Accounts / Sent / My sent per user.
          Turn Accounts off for smart-folder-only access (share the inbox smart folder with them).
        </p>
        <ShareAccountButton account={account} onShared={() => void refreshShares()} showLabel />
      </div>

      {isLoading ? (
        <div className={cn('py-6 text-center text-sm', colors.textMuted)} role="status">
          Loading shared users…
        </div>
      ) : list.length === 0 ? (
        <div
          className={cn(
            'rounded-xl border border-dashed py-10 px-4 text-center text-sm',
            colors.border,
            colors.textMuted,
          )}
        >
          <p className="font-medium text-gray-700 dark:text-gray-300">Not shared yet</p>
          <p className="mt-1">Use Share above to give other users access to this account.</p>
        </div>
      ) : (
        <div className={cn('rounded-xl border overflow-hidden', colors.border)}>
          <div className={cn(tableHeaderClass, 'grid-cols-[minmax(0,1fr)_auto_auto_auto]')}>
            <span>User</span>
            <span className="hidden sm:inline">Folders</span>
            <span className="text-center w-16">Access</span>
            <span className="w-10" />
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {list.map((share) => (
              <div
                key={share.shareId}
                className={cn(
                  'grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 items-center px-4 py-3',
                  colors.text,
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={cn(
                      'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center',
                      'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400',
                    )}
                  >
                    {UserIcon}
                  </span>
                  <span className="text-sm font-medium truncate">{share.sharedWithUserName}</span>
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 justify-end sm:justify-start">
                  {shareFolderSwitch(share, 'showInAccountsSidebar', 'Accounts')}
                  {shareFolderSwitch(share, 'showSentFolder', 'Sent')}
                  {shareFolderSwitch(share, 'showMySentFolder', 'My sent')}
                </div>

                <span
                  className={cn(
                    'inline-flex justify-center w-16 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                  )}
                >
                  {share.permission}
                </span>

                <div className="flex justify-end w-10">
                  <button
                    type="button"
                    disabled={rowBusy}
                    aria-busy={revokingShareId === share.shareId}
                    onClick={() => void handleRemoveShare(share.shareId)}
                    title={`Remove access for ${share.sharedWithUserName}`}
                    aria-label={`Remove share for ${share.sharedWithUserName}`}
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                      'text-gray-500 hover:bg-red-50 hover:text-red-600',
                      'dark:text-gray-400 dark:hover:bg-red-900/30 dark:hover:text-red-400',
                      'disabled:opacity-50 disabled:pointer-events-none',
                    )}
                  >
                    {revokingShareId === share.shareId ? (
                      <SpinnerIcon className="h-4 w-4" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
